import {
    useState,
    useRef,
    Dispatch,
    SetStateAction,
    useCallback,
    useMemo,
    useEffect,
    PropsWithChildren
} from "react";

import { useIntl } from "react-intl";

import { USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL } from "@atlassian/onboarding-survey";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";

import { Setup } from "..";
import { useCloudStatus } from "../../../hooks/use-cloud-status";
import { useConfirmationPage } from "../../../hooks/use-confirmation-page.tsx";
import {
    type CreateJiraFflProjectInput,
    useCreateJiraFflProject
} from "../../../hooks/use-create-jira-ffl-project";
import { useMatureExampleProjectWithExperiments } from "../../../hooks/use-mature-example-project-with-experiments";
import { TemplateQueryParamScrum } from "../../../utils/utils.ts";
import { getDefaultProjectName } from "../../onboarding-wizard/wizard/page-project-details/left-hand-side/utils.ts";
import { Jfind1663CreateProjectTaskPollingProvider as InviteWrapperProvider } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-create-project-task-polling-context.tsx";
import { useJfind1663FetchInterceptor } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-interceptor.tsx";
import { Invite } from "../common/steps/invite/invite";
import { Loading } from "../common/steps/loading/loading";
import { Preview } from "../common/steps/preview/preview";
import { SpaceDetails } from "../common/steps/space-details/space-details";
import {
    TemplateOptionKanban,
    TemplateOptionScrum,
    type StepContext
} from "../types.tsx";

import {
    SETUP_SCREEN_INDEX_FFL_INVITE,
    SETUP_SCREEN_INDEX_SPACE_DETAILS
} from "./constants";
import { useCgsPreviewData } from "./use-cgs-preview-data";
import { getDateDaysFromNow, onLoadingStep, useBulkEmailInvite } from "./utils";

export type JiraFflStepContext = StepContext;

export const JiraFflSetup = () => {
    const { cloudId: tenantId, createCloudStatus } = useCloudStatus();
    const { formatMessage } = useIntl();
    const { postBulkEmailInvite } = useBulkEmailInvite();
    const setContextRef = useRef<Dispatch<
        SetStateAction<JiraFflStepContext>
    > | null>(null);
    const pendingProjectCreateInputRef = useRef<
        CreateJiraFflProjectInput | undefined
    >(undefined);
    const hasTriggeredProjectCreationRef = useRef(false);
    const createCloudStatusRef = useRef(createCloudStatus);
    createCloudStatusRef.current = createCloudStatus;
    const pendingCloudReadyResolveRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (
            createCloudStatus === "success" &&
            pendingCloudReadyResolveRef.current
        ) {
            const resolve = pendingCloudReadyResolveRef.current;
            pendingCloudReadyResolveRef.current = null;
            resolve();
        }
    }, [createCloudStatus]);

    const waitForCloudSuccess = useCallback(async () => {
        if (createCloudStatusRef.current === "success") {
            return;
        }
        await new Promise<void>((resolve) => {
            pendingCloudReadyResolveRef.current = resolve;
            if (createCloudStatusRef.current === "success") {
                pendingCloudReadyResolveRef.current = null;
                resolve();
            }
        });
    }, []);
    const { setMatureSampleProjectEligibility } = useMatureSampleProject();

    const {
        templateChoice,
        jtbdTemplateOverride,
        teamType: teamTypeFromQueryParam,
        taskIdParam,
        jiraFflCgsKey
    } = useSearchParameters();

    const {
        userSegmentationContext: { selectedTeamType: teamTypeFromContext }
    } = useConfirmationPage();

    const teamType = useMemo(() => {
        const normalizeTeamType = (value?: string | null) =>
            value === "" || value === null ? undefined : value;

        return (
            normalizeTeamType(teamTypeFromContext) ??
            normalizeTeamType(teamTypeFromQueryParam) ??
            USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL
        );
    }, [teamTypeFromContext, teamTypeFromQueryParam]);

    const spaceName = useMemo(
        () => getDefaultProjectName(teamType ?? "", "", formatMessage),
        [teamType, formatMessage]
    );

    const cgsKey = useMemo(
        () =>
            jiraFflCgsKey && jiraFflCgsKey.trim() !== ""
                ? jiraFflCgsKey
                : undefined,
        [jiraFflCgsKey]
    );

    const template = useMemo(() => {
        switch (jtbdTemplateOverride ?? templateChoice) {
            case TemplateQueryParamScrum:
                return TemplateOptionScrum;
            default:
                return TemplateOptionKanban;
        }
    }, [jtbdTemplateOverride, templateChoice]);

    const { previewCardData, cgsData } = useCgsPreviewData(cgsKey, template);

    const { createJiraProject } = useCreateJiraFflProject({
        enabled: Boolean(cgsKey),
        cgsKey,
        onTaskIdCreated: (taskId) => {
            setContextRef.current?.((prev) => ({
                ...prev,
                taskId
            }));
        }
    });

    const onSpaceDetailsSubmitFfl = useCallback(
        async ({
            context,
            setContext
        }: {
            context: JiraFflStepContext;
            setContext: Dispatch<SetStateAction<JiraFflStepContext>>;
        }) => {
            setContextRef.current = setContext;
            await setMatureSampleProjectEligibility({
                context,
                setContext
            });
            pendingProjectCreateInputRef.current = {
                ...(cgsData ? { cgsData, projectName: context.spaceName } : {}),
                template: context.template
            };
            // Space Details submit button stays in a loading state until cloud is ready
            // and project creation is queued (see space-details await onSubmit).
            await waitForCloudSuccess();

            if (!pendingProjectCreateInputRef.current) {
                return;
            }

            if (hasTriggeredProjectCreationRef.current) {
                return;
            }

            hasTriggeredProjectCreationRef.current = true;

            const projectCreatePromise = createJiraProject(
                pendingProjectCreateInputRef.current
            );
            setContextRef.current?.((prevContext) => ({
                ...prevContext,
                promisesToResolve: prevContext.promisesToResolve.concat([
                    projectCreatePromise
                ])
            }));
        },
        [
            cgsData,
            createJiraProject,
            setMatureSampleProjectEligibility,
            waitForCloudSuccess
        ]
    );

    const onInviteSubmitFfl = useCallback(
        async ({
            context,
            setContext
        }: {
            context: JiraFflStepContext;
            setContext: Dispatch<SetStateAction<JiraFflStepContext>>;
        }) => {
            return postBulkEmailInvite({
                context: context as JiraFflStepContext,
                setContext: setContext as Dispatch<
                    SetStateAction<JiraFflStepContext>
                >
            });
        },
        [postBulkEmailInvite]
    );

    const onLoadingLoadFfl = useCallback(
        async ({
            context,
            setContext
        }: {
            context: JiraFflStepContext;
            setContext: Dispatch<SetStateAction<JiraFflStepContext>>;
        }) => {
            return onLoadingStep({
                context: context as JiraFflStepContext,
                setContext: setContext as Dispatch<
                    SetStateAction<JiraFflStepContext>
                >
            });
        },
        []
    );

    const initialContext: JiraFflStepContext = useMemo(() => {
        return {
            tenantId,
            teamType,
            spaceName: cgsData?.sampleIssues?.spaceName ?? spaceName,
            dueDate: getDateDaysFromNow(14),
            template,
            promisesToResolve: [],
            taskId: taskIdParam ?? undefined,
            previewCardData
        };
    }, [
        teamType,
        cgsData,
        tenantId,
        taskIdParam,
        previewCardData,
        template,
        spaceName
    ]);

    return (
        <InviteWrapperProvider>
            <InviteInterceptor>
                <Setup<JiraFflStepContext>
                    initialContext={initialContext}
                    initialScreenIndex={
                        taskIdParam
                            ? SETUP_SCREEN_INDEX_FFL_INVITE
                            : SETUP_SCREEN_INDEX_SPACE_DETAILS
                    }
                    screens={[
                        {
                            type: "split",
                            screenId: "space-details",
                            LeftComponent: SpaceDetails,
                            RightComponent: Preview,
                            onSubmit: onSpaceDetailsSubmitFfl
                        },
                        {
                            type: "split",
                            screenId: "invite",
                            LeftComponent: Invite,
                            RightComponent: Preview,
                            onSubmit: onInviteSubmitFfl
                        },
                        {
                            type: "split",
                            screenId: "loading",
                            LeftComponent: Loading,
                            RightComponent: Preview,
                            onLoad: onLoadingLoadFfl
                        }
                    ]}
                />
            </InviteInterceptor>
        </InviteWrapperProvider>
    );
};

const InviteInterceptor = ({ children }: PropsWithChildren) => {
    const { cloudId: tenantId } = useCloudStatus();
    useJfind1663FetchInterceptor({ cloudId: tenantId });
    return <>{children}</>;
};

const useMatureSampleProject = () => {
    const [isEligibleForSampleProject, setIsEligibleForSampleProject] =
        useState(false);

    // Create mature sample project only if eligible.
    useMatureExampleProjectWithExperiments(isEligibleForSampleProject);

    const setMatureSampleProjectEligibility = useCallback(
        async ({
            context
        }: {
            context: JiraFflStepContext;
            setContext: Dispatch<SetStateAction<JiraFflStepContext>>;
        }) => {
            setIsEligibleForSampleProject(context.template === "KANBAN");
        },
        []
    );

    return { setMatureSampleProjectEligibility };
};
